import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { CASH_CONVERSION_FEE } from "@/constants";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/utils";
import { posApiFetch } from "@/utils/pos-api";
import React, { useCallback, useEffect, useState } from "react";
import Decimal from "decimal.js";
import { provider } from "@/constants/walletConnectionConfig";
import { Loader2, ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatUnits } from "viem";
import useSWR from "swr";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const SENDER_ADDRESS = "0x0DF42F58a867b6032B52C4e33C3C9b36E2045b6A";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  walletAddress: string | null;
}

interface AdminUsersResponse {
  data: AdminUser[];
  meta: { currentPage: number; pageSize: number; total: number };
}

interface ISwapInfo {
  fromTokenAmount: string;
  toTokenAmount: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatShortAddress(addr: string | null | undefined) {
  if (!addr) return "";
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/* ------------------------------------------------------------------ */
/*  User-picker combobox                                               */
/* ------------------------------------------------------------------ */

function UserCombobox({
  users,
  value,
  onChange,
  placeholder,
  disabled,
}: {
  users: AdminUser[];
  value: string;
  onChange: (userId: string) => void;
  placeholder: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selected = users.find((u) => u.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal h-[36px] text-[13px] border-gray-200 bg-white text-[var(--primary-text-color)]"
        >
          {selected ? (
            <span className="truncate">
              {selected.displayName || selected.email}
              {selected.walletAddress && (
                <span className="text-gray-400 ml-1">
                  ({formatShortAddress(selected.walletAddress)})
                </span>
              )}
            </span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search user…" />
          <CommandList>
            <CommandEmpty>No users found.</CommandEmpty>
            <CommandGroup>
              {users.map((u) => (
                <CommandItem
                  key={u.id}
                  value={`${u.displayName || ""} ${u.email} ${u.walletAddress || ""}`}
                  onSelect={() => {
                    onChange(u.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-3.5 w-3.5",
                      value === u.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="text-[13px]">{u.displayName || u.email}</span>
                    {u.walletAddress && (
                      <span className="text-[11px] text-gray-400">
                        {formatShortAddress(u.walletAddress)}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/* ------------------------------------------------------------------ */
/*  Dialog component                                                   */
/* ------------------------------------------------------------------ */

interface EthToSechDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function EthToSechDialog({ open, onOpenChange }: EthToSechDialogProps) {
  const { toast } = useToast();

  /* Fetch users list */
  const { data: usersResponse } = useSWR<AdminUsersResponse>(
    open ? "/v1/admin/users" : null,
    async () => {
      const params = new URLSearchParams();
      params.append("pageSize", "500");
      const response = await posApiFetch(`/v1/admin/users?${params.toString()}`);
      return response.json();
    }
  );
  const allUsers = usersResponse?.data ?? [];
  const usersWithWallet = allUsers.filter((u) => !!u.walletAddress);

  /* Local state */
  const [recipientUserId, setRecipientUserId] = useState("");
  const [amount, setAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPriceLoading, setIsPriceLoading] = useState(false);
  const [sechAmount, setSechAmount] = useState(0);
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);
  const [swapInfo, setSwapInfo] = useState<ISwapInfo>({} as ISwapInfo);

  /* Sender ETH balance */
  const [senderBalance, setSenderBalance] = useState<string>("0");
  const [isBalanceLoading, setIsBalanceLoading] = useState(false);

  const recipientUser = usersWithWallet.find((u) => u.id === recipientUserId);

  /* Fetch sender ETH balance when dialog opens */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsBalanceLoading(true);
    provider
      .getBalance(SENDER_ADDRESS)
      .then((bal) => {
        if (!cancelled) setSenderBalance(formatUnits(bal, 18));
      })
      .catch(() => {
        if (!cancelled) setSenderBalance("0");
      })
      .finally(() => {
        if (!cancelled) setIsBalanceLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  /* Reset form when dialog closes */
  useEffect(() => {
    if (!open) {
      setRecipientUserId("");
      setAmount("");
      setSechAmount(0);
      setSwapInfo({} as ISwapInfo);
    }
  }, [open]);

  /* Derived calculations */
  const amountDecimal = new Decimal(amount || 0);
  const feeAmount = amountDecimal.mul(CASH_CONVERSION_FEE).div(100).toDecimalPlaces(2, Decimal.ROUND_DOWN);
  const netAmount = amountDecimal.sub(feeAmount).toDecimalPlaces(2, Decimal.ROUND_DOWN);
  const exchangeRate = new Decimal(swapInfo?.fromTokenAmount || 0)
    .div(new Decimal(swapInfo?.toTokenAmount || 1))
    .toDecimalPlaces(4, Decimal.ROUND_DOWN);
  const userReceive = new Decimal(sechAmount || 0).toDecimalPlaces(4, Decimal.ROUND_DOWN);
  const senderSpend = new Decimal(swapInfo?.fromTokenAmount || 0).toDecimalPlaces(18);
  const senderBalanceDecimal = new Decimal(senderBalance || 0).toDecimalPlaces(6);
  const isBalanceInsufficient =
    !!sechAmount &&
    new Decimal(swapInfo?.fromTokenAmount || 0).greaterThan(new Decimal(senderBalance || 0));

  /* Price lookup */
  const getSECHAmount = useCallback(
    async (usdAmount?: string) => {
      const exchangeInfoResponse = await apiFetch(
        `/v1/cash-swap-amounts?amountIn=${usdAmount || amount}`
      );
      const { data } = await exchangeInfoResponse.json();
      setSechAmount(Number(data?.toTokenAmount));
      setSwapInfo({
        fromTokenAmount: data?.fromTokenAmount,
        toTokenAmount: data?.toTokenAmount,
      });
    },
    [amount]
  );

  const handleCashAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const usdAmount = e.target.value;
    if (/^\d+\.\d{4,}$/.test(usdAmount)) return;
    setAmount(usdAmount);

    if (!parseFloat(usdAmount)) {
      setSechAmount(0);
      return;
    }
    setIsPriceLoading(true);
    if (debounceTimeout) clearTimeout(debounceTimeout);
    setDebounceTimeout(
      setTimeout(() => {
        getSECHAmount(usdAmount)
          .catch((err) => console.error("Failed to fetch tokens amount.", err))
          .finally(() => setIsPriceLoading(false));
      }, 1000)
    );
  };

  /* Submit */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!recipientUser?.walletAddress) {
      toast({ title: "Error", description: "Please select a recipient user", variant: "destructive" });
      return;
    }
    if (Number(amount) < 0.001) {
      toast({ title: "Error", description: "Amount should be at least 0.001 USD", variant: "destructive" });
      return;
    }

    try {
      setIsProcessing(true);
      if (!swapInfo.fromTokenAmount || !swapInfo.toTokenAmount) {
        throw new Error("Missing necessary data for conversion");
      }

      const response = await posApiFetch("/v1/admin/cash-to-token", {
        method: "POST",
        body: JSON.stringify({
          cashAmount: amount,
          senderAddress: SENDER_ADDRESS,
          recipientAddress: recipientUser.walletAddress,
        }),
      });

      const result = await response?.json();
      if (response.ok) {
        toast({
          title: "Swap Successful",
          description: "Tokens sent to the recipient wallet",
        });
        onOpenChange(false);
        // Refresh sender balance
        provider
          .getBalance(SENDER_ADDRESS)
          .then((bal) => setSenderBalance(formatUnits(bal, 18)))
          .catch(() => {});
      } else {
        let errorMessage;
        if (Object.keys(result?.error?.message?.fieldErrors || {})?.length) {
          errorMessage = Object.values(result?.error?.message?.fieldErrors).flat().join(", ");
        } else {
          errorMessage = result?.error?.message || `Code: ${result?.error?.code}. Unexpected error`;
        }
        toast({ title: "Swap Failed", description: errorMessage, variant: "destructive" });
      }
    } catch (error: any) {
      console.error(error);
      toast({
        title: error?.title || "Error",
        description: `Failed to swap tokens. ${error?.details || error?.message}`,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const canSubmit =
    !isProcessing && !isPriceLoading && !isBalanceInsufficient && !!recipientUserId && Number(amount) >= 0.001;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] p-0 gap-0 bg-white border-gray-200">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="text-[16px] font-semibold text-[var(--primary-text-color)]">
            ETH to SECH
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-5 pb-5 space-y-4">
          {/* Sender info */}
          <div className="rounded-[6px] border border-gray-200 bg-gray-50 px-3 py-2.5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-gray-400 mb-0.5">
                  From
                </div>
                <div className="text-[13px] font-medium text-[var(--primary-text-color)]">
                  {formatShortAddress(SENDER_ADDRESS)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-medium uppercase tracking-wider text-gray-400 mb-0.5">
                  Balance
                </div>
                <div className="text-[13px] font-medium text-[var(--primary-text-color)]">
                  {isBalanceLoading ? "…" : `${senderBalanceDecimal.toString()} ETH`}
                </div>
              </div>
            </div>
          </div>

          {/* Recipient */}
          <div className="space-y-1.5">
            <label className="text-[13px] font-medium text-[var(--primary-text-color)]">
              To (receives SECH)
            </label>
            <UserCombobox
              users={usersWithWallet}
              value={recipientUserId}
              onChange={setRecipientUserId}
              placeholder="Select recipient…"
            />
            {recipientUser?.walletAddress && (
              <p className="text-[11px] text-gray-400 font-mono">
                {recipientUser.walletAddress}
              </p>
            )}
          </div>

          {/* Amount */}
          <div className="space-y-1.5">
            <label className="text-[13px] font-medium text-[var(--primary-text-color)]">
              Amount (USD)
            </label>
            <Input
              type="number"
              step="any"
              placeholder="0.00"
              value={amount}
              onChange={handleCashAmountChange}
              className="h-[36px] text-[13px] border-gray-200"
            />
          </div>

          {/* Transaction details */}
          {!isPriceLoading && !!amount && Number(amount) > 0 && (
            <div className="rounded-[6px] border border-gray-200 bg-gray-50 px-3 py-2.5 space-y-1.5 text-[13px]">
              <div className="flex justify-between">
                <span className="text-gray-500">Fee ({CASH_CONVERSION_FEE}%)</span>
                <span className="text-[var(--primary-text-color)]">${feeAmount.toString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Net amount</span>
                <span className="text-[var(--primary-text-color)]">${netAmount.toString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Rate</span>
                <span className="text-[var(--primary-text-color)]">1 SECH = ${exchangeRate.toString()}</span>
              </div>
              <div className="flex justify-between pt-1.5 border-t border-gray-200">
                <span className="font-medium text-[var(--primary-text-color)]">Recipient gets</span>
                <span className="font-medium text-[var(--primary-text-color)]">~{userReceive.toString()} SECH</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-[var(--primary-text-color)]">Sender spends</span>
                <span className="font-medium text-[var(--primary-text-color)]">~{senderSpend.toString()} ETH</span>
              </div>
              {isBalanceInsufficient && (
                <div className="text-[12px] text-red-600 font-medium pt-0.5">Insufficient sender balance</div>
              )}
            </div>
          )}

          {isPriceLoading && (
            <div className="flex items-center gap-2 text-[13px] text-gray-400 py-1">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Calculating…
            </div>
          )}

          {/* Submit */}
          <Button type="submit" disabled={!canSubmit} className="w-full h-[36px] text-[13px]">
            {isProcessing ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Processing…
              </>
            ) : isBalanceInsufficient ? (
              "Insufficient Balance"
            ) : (
              "Convert"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
